// Мн1–Мн7: the tree of the tracker site — section → tab → block. The one list the menu, the tabs and the old addresses
// are drawn from; `widget/tests/tracker-structure.test.ts` checks it against quint/specs/tracker_structure.qnt.
// The project is the root, the rest are ways to show it (reports 271, 270, 267, 258, 254, 215).

/**
 * Who looks: `super` — super admin who sees all projects, `operator` — such a super admin who also registers projects,
 * `scoped` — super admin of one project (no «Возможности», Мн8), `admin` — the rest.
 */
export type TreeRole = 'super' | 'operator' | 'scoped' | 'admin';

export interface TabDef {
  key: string;
  label: string;
  /** What the tab shows — the block names of the model (Ра8: «Профиль» shows two). */
  blocks: string[];
}

export interface SectionDef {
  key: string;
  label: string;
  /** Menu group: everything of the project stands under the project, the shared area of the tracker stands apart (Тг6). */
  group: 'project' | 'tracker';
  /** Without tabs the section shows its `blocks` itself. */
  tabs: TabDef[];
  blocks: string[];
}

export const SECTIONS: SectionDef[] = [
  {
    key: 'tasks',
    label: 'Задачи',
    group: 'project',
    tabs: [
      { key: 'board', label: 'Доска', blocks: ['board'] },
      { key: 'list', label: 'Список', blocks: ['list'] },
      // Пов12: the summary of repeats by themes, a view over the same reports.
      { key: 'themes', label: 'Сводка по темам', blocks: ['themes'] },
    ],
    blocks: [],
  },
  {
    key: 'epics',
    label: 'Эпики',
    group: 'project',
    tabs: [
      { key: 'backlog', label: 'Бэклог', blocks: ['epicBacklog'] },
      { key: 'work', label: 'В работе и готово', blocks: ['epicsStarted'] },
    ],
    blocks: [],
  },
  { key: 'questions', label: 'Вопросы', group: 'project', tabs: [], blocks: ['questions'] },
  { key: 'ux', label: 'UX-сессии', group: 'project', tabs: [], blocks: ['ux'] },
  {
    key: 'project',
    label: 'Проект',
    group: 'project',
    tabs: [
      // Ра8: «Профиль» и «Промпты» — одна вкладка с двумя блоками. Тг1: в проекте только то, что настраивают у приложения.
      { key: 'profile', label: 'Профиль', blocks: ['profile', 'prompts'] },
      { key: 'voice', label: 'Голос', blocks: ['voice'] },
      { key: 'features', label: 'Возможности', blocks: ['features'] },
    ],
    blocks: [],
  },
  {
    // The key stays `registry` (addresses, icons); the area is the shared one: Тг1 — projects, access, people, host keys.
    key: 'registry',
    label: 'Настройки',
    group: 'tracker',
    tabs: [
      { key: 'projects', label: 'Проекты', blocks: ['registry'] },
      { key: 'mcp', label: 'Доступ MCP', blocks: ['mcp'] },
      { key: 'users', label: 'Пользователи', blocks: ['users'] },
      { key: 'host', label: 'Ключ хоста', blocks: ['hostKey'] },
      // Вк2: the catalog of tracker builds is shared by all projects — a tab of the shared area, for those who see all of them.
      { key: 'versions', label: 'Версии', blocks: ['versions'] },
    ],
    blocks: [],
  },
];

const SECTION_KEYS = new Set(SECTIONS.map((section) => section.key));

/** Which sections a role opens. Admin — own questions and UX sessions; the registry — only the operator. */
const ROLE_SECTIONS: Record<TreeRole, string[]> = {
  admin: ['questions', 'ux'],
  scoped: ['tasks', 'epics', 'questions', 'ux', 'project', 'registry'],
  super: ['tasks', 'epics', 'questions', 'ux', 'project', 'registry'],
  operator: ['tasks', 'epics', 'questions', 'ux', 'project', 'registry'],
};

export function treeRole(me: { isSuper: boolean; operator?: boolean; manager?: boolean }): TreeRole {
  if (!me.isSuper) return 'admin';
  if (me.manager === false && !me.operator) return 'scoped';
  return me.operator ? 'operator' : 'super';
}

export function sectionsOf(role: TreeRole): SectionDef[] {
  return SECTIONS.filter((section) => ROLE_SECTIONS[role].includes(section.key));
}

/** The tabs of a section for a role: «Возможности» and «Версии» only for those who see all projects (Мн8, Вк2), «Проекты» only for the operator (Тг5). */
function tabsOfRole(section: SectionDef, role: TreeRole): TabDef[] {
  if (section.key === 'project' && (role === 'scoped' || role === 'admin')) return section.tabs.filter((tab) => tab.key !== 'features');
  if (section.key === 'registry') {
    const seesAll = role === 'operator' || role === 'super';
    return section.tabs.filter((tab) => (tab.key !== 'projects' || role === 'operator') && (tab.key !== 'versions' || seesAll));
  }
  return section.tabs;
}

/** The sections of a person: a registrar who is not a super admin has no reports, only «Пользователи» of the project and the registry. */
export function sectionsFor(me: { isSuper: boolean; operator?: boolean; manager?: boolean }): SectionDef[] {
  if (me.operator && !me.isSuper) return SECTIONS.filter((section) => ['questions', 'ux', 'registry'].includes(section.key));
  return sectionsOf(treeRole(me));
}

/** The model's tree of a role: (section, tab, block); the tab is `''` where the section has none. */
export function treeOf(role: TreeRole): [string, string, string][] {
  return sectionsOf(role).flatMap((section) => [
    ...tabsOfRole(section, role).flatMap((tab) => tab.blocks.map((block): [string, string, string] => [section.key, tab.key, block])),
    ...section.blocks.map((block): [string, string, string] => [section.key, '', block]),
  ]);
}

/** Мн7: the places the old addresses (`#backlog`, `#board`, `#all`, …) lead to — and the new `#section/tab`. */
const OLD_HASHES: Record<string, { section: string; tab: string; stage?: 'backlog' }> = {
  reports: { section: 'tasks', tab: 'board' },
  board: { section: 'tasks', tab: 'board' },
  backlog: { section: 'tasks', tab: 'list', stage: 'backlog' },
  all: { section: 'tasks', tab: 'list' },
  epicbacklog: { section: 'epics', tab: 'backlog' },
  epics: { section: 'epics', tab: 'work' },
  settings: { section: 'project', tab: 'profile' },
  users: { section: 'registry', tab: 'users' },
  projects: { section: 'registry', tab: 'projects' },
};

export interface Place {
  section: string;
  tab: string;
  /** `#backlog` opens the list already filtered by the first stage. */
  stage?: 'backlog';
  /** Мн9: the project the address names (`#@city/tasks/list`); none — the project the browser remembers. */
  project?: string;
}

export const OLD_HASH_KEYS = Object.keys(OLD_HASHES);

/** `#tasks/list`, `#epics`, or an old address; `#@project/…` names the project (Мн9); nothing known — `null`. */
export function placeOfHash(hash: string): Place | null {
  let raw = hash.replace(/^#/, '');
  let project: string | undefined;
  if (raw.startsWith('@')) {
    const cut = raw.indexOf('/');
    try {
      project = decodeURIComponent(raw.slice(1, cut < 0 ? undefined : cut));
    } catch {
      return null;
    }
    raw = cut < 0 ? '' : raw.slice(cut + 1);
    if (!project) return null;
    if (!raw) return { section: 'tasks', tab: 'board', project };
  }
  const place = placeOfPlainHash(raw);
  return place && project ? { ...place, project } : place;
}

function placeOfPlainHash(raw: string): Place | null {
  if (raw === 'news') return { section: 'news', tab: '' };
  const [head, ...restParts] = raw.split('/');
  const rest = restParts.join('/');
  if (OLD_HASHES[raw]) return OLD_HASHES[raw];
  if (!SECTION_KEYS.has(head)) return null;
  const section = SECTIONS.find((s) => s.key === head)!;
  const [tabKey, stage] = rest.split('/');
  // Тг1: the old tabs of «Проект» that moved to the shared area keep their addresses.
  if (head === 'project' && ['mcp', 'users', 'host'].includes(tabKey)) return { section: 'registry', tab: tabKey };
  const tab = section.tabs.find((t) => t.key === tabKey)?.key ?? section.tabs[0]?.key ?? '';
  return { section: head, tab, stage: head === 'tasks' && tab === 'list' && stage === 'backlog' ? 'backlog' : undefined };
}

/** The address of a place: the board is the home (empty address), the rest are `#section/tab`; a project that is not the person's own is named first (Мн9). */
export function hashOfPlace(place: Place): string {
  const named = place.project ? `@${encodeURIComponent(place.project)}` : '';
  const path =
    place.section === 'tasks' && place.tab === 'board'
      ? ''
      : place.tab
        ? `${place.section}/${place.tab}${place.stage ? `/${place.stage}` : ''}`
        : place.section;
  if (!named) return path ? `#${path}` : '';
  return `#${named}${path ? `/${path}` : ''}`;
}

/** The tabs of a section a role may open (a registrar who is not super sees only «Проекты» and «Пользователи» of the shared area). */
export function tabsOf(section: SectionDef, me: { isSuper: boolean; operator?: boolean; manager?: boolean }): TabDef[] {
  if (section.key === 'registry' && !me.isSuper) return section.tabs.filter((tab) => tab.key === 'projects' || tab.key === 'users');
  return tabsOfRole(section, treeRole(me));
}
